<!--
  ClientContactCard.vue -- the "Client contact" card on the Client detail page.

  Jake, 2026-10-19: the contact (name, email, phone) gets its own card, apart
  from the People list (which is the extra people the client invited to their
  portal).

    * Name  -- READ-ONLY and DERIVED from the client's primary-contact portal
               account (``contact.contact_name``). No column of its own. Blank
               until the client has a portal account: shown as "--", never the
               company name or the email's local part.
    * Email -- editable. It is the portal LOGIN IDENTITY, so changing it moves
               the login and re-issues the invite (Client._s_patch). It
               therefore saves on blur / Enter ONLY, never per keystroke, and
               a value that is not a complete address is never sent.
    * Phone -- editable, debounce auto-save (no side effects).

  Saving rides the clients workspace store's ``saveClientField`` (the same
  pending/saving/saved/error vocabulary as the Notes editor).
-->
<template>
    <div class="ccc" data-testid="client-contact-card">
        <div class="ccc__row">
            <span class="ccc__label">Name</span>
            <div class="ccc__control">
                <span class="ccc__name" data-testid="client-contact-name">
                    {{ contact && contact.contact_name ? contact.contact_name : '--' }}
                </span>
                <p class="ccc__hint" data-testid="client-contact-name-hint">
                    From the client's portal account. Add or change people in the People list below.
                </p>
            </div>
        </div>

        <label class="ccc__row">
            <span class="ccc__label">Email</span>
            <div class="ccc__control">
                <input
                    v-model="emailDraft"
                    type="email"
                    class="ccc__input"
                    :class="{ 'ccc__input--error': Boolean(emailError) }"
                    :disabled="!canEdit"
                    data-testid="client-contact-email"
                    @blur="commitEmail"
                    @keydown.enter.prevent="commitEmail"
                />
                <p
                    v-if="contact && contact.has_portal_account"
                    class="ccc__warning"
                    data-testid="client-contact-email-warning"
                >
                    Changing this moves the client's portal login to the new address and sends a new invite.
                </p>
                <p
                    v-if="emailError"
                    class="ccc__error"
                    data-testid="client-contact-email-error"
                >
                    {{ emailError }}
                </p>
                <p class="ccc__status" data-testid="client-contact-email-status">
                    {{ statusLabel(emailState) }}
                </p>
            </div>
        </label>

        <label class="ccc__row">
            <span class="ccc__label">Phone</span>
            <div class="ccc__control">
                <input
                    v-model="phoneDraft"
                    type="tel"
                    class="ccc__input"
                    :disabled="!canEdit"
                    data-testid="client-contact-phone"
                    @input="onPhoneInput"
                />
                <p
                    v-if="phoneState === 'error'"
                    class="ccc__error"
                    data-testid="client-contact-phone-error"
                >
                    {{ phoneErrorText }}
                </p>
                <p class="ccc__status" data-testid="client-contact-phone-status">
                    {{ statusLabel(phoneState) }}
                </p>
            </div>
        </label>
    </div>
</template>

<script>
import { useClientsWorkspaceStore } from '@/stores/clientsWorkspace'

// A complete-address check, not RFC validation: the server is the authority
// (it 422s a duplicate or malformed address). This only stops a half-typed
// value ("sarah@") from ever leaving the browser.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
    name: 'ClientContactCard',
    props: {
        clientId: { type: [Number, String], required: true },
        // The client row (email + phone as last loaded).
        client: { type: Object, required: true },
        // GET /clients/:id/contact: { contact_name, has_portal_account, ... }
        contact: { type: Object, default: null },
        canEdit: { type: Boolean, default: false },
    },
    emits: ['saved'],
    data() {
        return {
            emailDraft: this.client.email || '',
            phoneDraft: this.client.phone || '',
            // The last value known to be on the server, per field.
            savedEmail: this.client.email || '',
            savedPhone: this.client.phone || '',
            localEmailError: '',
        }
    },
    computed: {
        store() {
            return useClientsWorkspaceStore()
        },
        emailState() {
            return this.store.fieldSaveState[`${this.clientId}:email`] || 'idle'
        },
        phoneState() {
            return this.store.fieldSaveState[`${this.clientId}:phone`] || 'idle'
        },
        emailError() {
            if (this.localEmailError) return this.localEmailError
            return this.emailState === 'error'
                ? this.store.fieldSaveError[`${this.clientId}:email`]
                : ''
        },
        phoneErrorText() {
            return this.store.fieldSaveError[`${this.clientId}:phone`] || ''
        },
    },
    watch: {
        // A reload / save echo changes the prop. Never let it overwrite what
        // the user is typing: sync the draft only when it has no unsaved edit
        // (email: draft still equals the last known server value; phone: no
        // pending/saving auto-save for the field).
        'client.email'(v) {
            if (this.emailDraft === this.savedEmail) {
                this.emailDraft = v || ''
            }
            this.savedEmail = v || ''
        },
        'client.phone'(v) {
            const busy =
                this.phoneState === 'pending' || this.phoneState === 'saving'
            if (!busy && this.phoneDraft === this.savedPhone) {
                this.phoneDraft = v || ''
            }
            this.savedPhone = v || ''
        },
    },
    methods: {
        statusLabel(state) {
            if (state === 'pending') return 'Editing...'
            if (state === 'saving') return 'Saving...'
            if (state === 'saved') return 'Saved'
            if (state === 'error') return 'Could not save. Try again.'
            return ''
        },
        async commitEmail() {
            if (!this.canEdit) return
            const next = (this.emailDraft || '').trim()
            if (next === this.savedEmail) {
                this.localEmailError = ''
                return
            }
            if (next && !EMAIL_SHAPE.test(next)) {
                this.localEmailError = 'Enter a valid email address.'
                return
            }
            this.localEmailError = ''
            const ok = await this.store.saveClientField(
                this.clientId,
                'email',
                next || null,
                { delayMs: 0 },
            )
            if (ok) {
                this.savedEmail = next
                this.$emit('saved', { field: 'email', value: next || null })
            }
        },
        onPhoneInput() {
            if (!this.canEdit) return
            const next = (this.phoneDraft || '').trim()
            // Baseline = what the server has or is about to have: the last
            // value sent, else the loaded one. Back to it means nothing to
            // save, and any not-yet-sent edit must be dropped, not left armed.
            const sent = this.store.lastSentValue(this.clientId, 'phone')
            const baseline = sent !== undefined ? sent || '' : this.savedPhone
            if (next === baseline) {
                this.store.cancelClientField(this.clientId, 'phone')
                return
            }
            this.store
                .saveClientField(this.clientId, 'phone', next || null)
                .then((ok) => {
                    if (ok) {
                        this.$emit('saved', {
                            field: 'phone',
                            value: next || null,
                        })
                    }
                })
        },
    },
}
</script>

<style scoped>
.ccc {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.ccc__row {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 12px;
    align-items: start;
    font-size: 13px;
}
.ccc__label {
    color: var(--tf-fg-2);
    padding-top: 8px;
}
.ccc__control {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 420px;
}
.ccc__name {
    padding-top: 8px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.ccc__input {
    padding: 8px 10px;
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-xs, 4px);
    font-size: 13px;
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-1);
}
.ccc__input:disabled {
    background: var(--tf-surface-alt, #f7f8fa);
}
.ccc__input--error {
    border-color: #c0392b;
}
.ccc__hint,
.ccc__warning,
.ccc__status {
    margin: 0;
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
}
.ccc__warning {
    color: #8a5a00;
}
.ccc__status {
    min-height: 16px;
}
.ccc__error {
    margin: 0;
    font-size: 12px;
    color: #c0392b;
}
</style>
